import { Pause, Play } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import { showDate } from '../../../shared/tree'
import { Avatar } from '@/components/tree/Avatar'
import { peopleIn, span, type Album, type AlbumPhoto } from '@/lib/albums'
import { chapters, coverOf, ease, OTHER_TIMES, playPlan, TYPE_MS, type PlayStop } from '@/lib/storybook'
import { cn } from '@/lib/utils'
import { StoryImg } from './PickedUp'

// A story to scroll, a chapter at a time. Each chapter's first photo fills the screen and settles as the reader
// scrolls, the chapter's other photos (and any notes, such as moments) drift up over it with their words, and the
// faces of everyone in it gather at the bottom. Words of the keeper's own can light up a word at a time after the first
// chapter. Everything moves with the reader's thumb, unless they press Play: then it tells itself, lingering on each
// photo and sliding on to the next, until it ends or they touch it.
export type StoryChapter = {
  key: string
  heading: string
  sub?: string
  photos: AlbumPhoto[]
  people: ReturnType<typeof peopleIn>
  // Behind the heading when the chapter has no photo of its own to show there.
  backdrop?: ReactNode
  // Drifting after the first photo's words (a year's moments), and after everything else.
  notes?: ReactNode
  coda?: ReactNode
}
export function ScrollStory({
  cover,
  backdrop,
  eyebrow,
  title,
  byline,
  chapters: parts,
  words,
  ending,
  onPick,
  testId,
}: {
  cover: AlbumPhoto | null
  backdrop?: ReactNode
  eyebrow?: string
  title: string
  byline: string
  chapters: StoryChapter[]
  words?: ReactNode
  ending: ReactNode
  onPick: (photo: AlbumPhoto) => void
  testId: string
}) {
  const scroller = useRef<HTMLDivElement>(null)
  const [playing, setPlaying] = useState(false)
  const woven = parts.flatMap((x) => x.photos)

  // Each section learns how far through it the reader is (--p, 0 to 1). Written straight to the page on each frame
  // rather than through React, so scrolling stays smooth.
  useEffect(() => {
    const box = scroller.current
    if (!box) return
    let frame = 0
    const tick = () => {
      const height = box.clientHeight
      for (const section of box.querySelectorAll<HTMLElement>('[data-progress]')) {
        const top = section.offsetTop - box.scrollTop
        const p = Math.min(1, Math.max(0, -top / Math.max(1, section.offsetHeight - height)))
        section.style.setProperty('--p', p.toFixed(3))
      }
    }
    const onScroll = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(tick)
    }
    tick()
    box.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      cancelAnimationFrame(frame)
      box.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [])

  usePlay(scroller, playing, () => setPlaying(false))

  return (
    <>
      <div ref={scroller} className="sb-chapters" data-testid={testId} data-playing={playing || undefined}>
        <section className="sb-cover" data-progress="cover" data-stop="cover">
          {cover ? <StoryImg photo={cover} sizes="112vw" eager className="sb-bg" alt="" /> : backdrop}
          <div className="sb-shade" />
          <div className="sb-cover-text flex flex-col gap-2">
            {eyebrow && <p className="eyebrow text-viewer-muted">{eyebrow}</p>}
            <h1 className="text-display text-balance">{title}</h1>
            <p className="text-sm text-viewer-muted">{byline}</p>
          </div>
          <p className="sb-hint" aria-hidden="true">
            Scroll to begin, or press Play
          </p>
        </section>
        {parts.map((chapter) => (
          <ChapterSection key={chapter.key} chapter={chapter} onPick={onPick} />
        ))}
        {woven.length > 0 && <Tapestry photos={woven} eyebrow={eyebrow} title={title} byline={byline} words={words} />}
        <section
          className="mx-auto flex max-w-2xl flex-col gap-6 px-6 pt-24 pb-[calc(var(--safe-bottom)+4rem)]"
          data-stop={woven.length ? undefined : 'end'}
        >
          {!woven.length && words}
          {ending}
        </section>
      </div>
      <button
        type="button"
        onClick={() => setPlaying((x) => !x)}
        className="absolute right-3 bottom-[calc(var(--safe-bottom)+0.75rem)] z-10 flex h-11 items-center gap-1.5 rounded-full bg-viewer/60 pr-4 pl-3 text-sm font-semibold text-viewer-foreground backdrop-blur-md transition-colors outline-none hover:bg-viewer/80 focus-visible:ring-[3px] focus-visible:ring-ring/60 sm:right-5"
      >
        {playing ? <Pause className="size-4" aria-hidden /> : <Play className="size-4" aria-hidden />}
        {playing ? 'Pause' : 'Play'}
      </button>
    </>
  )
}

// What the story stops on when it plays, in the order it reaches them: the cover, each chapter's opening, everything
// drifting through a chapter (photos, moments, captions) and the ending.
const STOPS = '[data-stop], .sb-drift > :not([aria-hidden])'

// Plays the story: works out its stops from the page as it is now, then scrolls through them a frame at a time. The
// stop it's lingering on is marked (data-held) so its photo can grow while it's there. Any touch, wheel or key from the
// reader stops it where it is, so they can carry on by hand.
function usePlay(scroller: React.RefObject<HTMLDivElement | null>, playing: boolean, stop: () => void) {
  const stopRef = useRef(stop)
  useEffect(() => {
    stopRef.current = stop
  })
  useEffect(() => {
    const box = scroller.current
    if (!playing || !box) return
    const els = [...box.querySelectorAll<HTMLElement>(STOPS)]
    const origin = box.getBoundingClientRect().top - box.scrollTop
    // The words each photo or moment types out, and the keeper's words at the end (a chapter's own photos type theirs
    // as they're reached).
    const typed = els.map((el) =>
      el.dataset.stop && el.dataset.stop !== 'end' ? [] : [...el.querySelectorAll<HTMLElement>('[data-type]')],
    )
    const stops: PlayStop[] = els.map((el, i) => {
      const r = el.getBoundingClientRect()
      return {
        kind: (el.dataset.stop as PlayStop['kind'] | undefined) ?? 'item',
        top: r.top - origin,
        height: r.height,
        chars: typed[i]!.reduce((n, x) => n + (x.dataset.type?.length ?? 0), 0),
      }
    })
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const steps = playPlan(stops, box.clientHeight, box.scrollHeight - box.clientHeight, box.scrollTop, still)
    // Whatever is still to come starts unwritten, so each caption can be typed as its photo arrives.
    if (!still) for (const x of typed.slice(steps[0]?.stop ?? els.length).flat()) x.textContent = ''
    const type = (stop: number, ms: number) => {
      let left = Math.floor(ms / TYPE_MS)
      for (const x of typed[stop] ?? []) {
        const text = x.dataset.type ?? ''
        const shown = text.slice(0, Math.max(0, left))
        if (x.textContent !== shown) x.textContent = shown
        left -= text.length
      }
    }
    let i = 0
    let began = performance.now()
    let held: HTMLElement | undefined
    let frame = requestAnimationFrame(function run(now) {
      const step = steps[i]
      if (!step) return stopRef.current()
      const t = step.ms ? Math.min(1, (now - began) / step.ms) : 1
      box.scrollTop = step.from + (step.to - step.from) * ease[step.pace](t)
      const el = step.pace === 'linger' ? els[step.stop] : undefined
      if (el !== held) {
        held?.removeAttribute('data-held')
        el?.setAttribute('data-held', '')
        held = el
      }
      if (el && !still) type(step.stop, now - began)
      if (t >= 1) {
        i++
        began = now
      }
      frame = requestAnimationFrame(run)
    })
    const halt = () => stopRef.current()
    const events = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const
    for (const e of events) box.addEventListener(e, halt, { passive: true })
    return () => {
      cancelAnimationFrame(frame)
      held?.removeAttribute('data-held')
      for (const x of typed.flat()) x.textContent = x.dataset.type ?? ''
      for (const e of events) box.removeEventListener(e, halt)
    }
  }, [playing, scroller])
}

// Everyone in the story, each a way to their page, with how many of its photos (or whatever it holds) they're in.
export function Cast({
  people,
  said = (n) => (n === 1 ? 'in 1 photo' : `in ${n} photos`),
}: {
  people: ReturnType<typeof peopleIn>
  said?: (n: number) => string
}) {
  if (!people.length) return null
  return (
    <>
      <h2 className="text-title">The people in this story</h2>
      <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        {people.map((person) => (
          <li key={person.id}>
            <Link
              to={`/person/${person.id}`}
              className="flex items-center gap-3 rounded-2xl outline-none focus-visible:ring-[3px] focus-visible:ring-ring/60"
            >
              <Avatar person={person} className="size-11 text-base" />
              <span className="min-w-0">
                <span className="block truncate font-medium">{person.name}</span>
                <span className="block text-xs text-viewer-muted">
                  {person.living === false ? 'In memory · ' : ''}
                  {said(person.photos)}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </>
  )
}

// An album's story, a year to a chapter, ending on every photo woven together under its name and the keeper's words.
export function Chapters({ album, onPick }: { album: Album; onPick: (photo: AlbumPhoto) => void }) {
  const dated = album.photos.flatMap((x) => (x.takenOn ? [x.takenOn] : [])).sort()
  const when = span(dated[0] ?? null, dated.at(-1) ?? null)
  return (
    <ScrollStory
      testId="story-chapters"
      cover={coverOf(album)}
      eyebrow={[album.collection?.title, when].filter(Boolean).join(' · ') || undefined}
      title={album.title}
      byline={`${album.by ? `From the album of ${album.by} · ` : ''}${album.photos.length === 1 ? '1 photo' : `${album.photos.length} photos`}`}
      chapters={chapters(album.photos).map((chapter) => ({
        key: chapter.year ?? 'undated',
        heading: chapter.year ?? OTHER_TIMES,
        photos: chapter.photos,
        people: chapter.people,
      }))}
      words={album.description.trim() ? <Words text={album.description.trim()} by={album.by} /> : undefined}
      onPick={onPick}
      ending={
        <>
          <Cast people={peopleIn(album.photos)} />
          <p className="border-t border-viewer-foreground/15 pt-5 text-sm text-viewer-muted">
            {album.by ? `From the album of ${album.by}. ` : ''}Every photo here is in the album as it was added. Tap one
            to pick it up and see the original.
          </p>
          <Link to={`/album/${album.id}`} className="self-start text-sm underline underline-offset-4">
            Back to the album
          </Link>
        </>
      }
    />
  )
}

function ChapterSection({ chapter, onPick }: { chapter: StoryChapter; onPick: (photo: AlbumPhoto) => void }) {
  const [hero, ...rest] = chapter.photos
  return (
    <section className="relative" data-progress="chapter" data-stop="chapter" aria-label={chapter.heading}>
      <div className="sb-pin">
        {hero ? <StoryImg photo={hero} sizes="112vw" className="sb-bg" alt="" /> : chapter.backdrop}
        <div className="sb-shade" />
        <div className="sb-year">
          <h2>{chapter.heading}</h2>
          {chapter.sub && <p className="mt-2 text-lg font-medium tracking-normal">{chapter.sub}</p>}
        </div>
        {chapter.people.length > 0 && (
          <div className="sb-faces">
            {chapter.people.slice(0, 8).map((person) => (
              <Avatar key={person.id} person={person} className="size-9 text-sm ring-2 ring-viewer-foreground/85" />
            ))}
            <span className="text-xs text-viewer-muted">
              {chapter.people
                .slice(0, 8)
                .map((x) => x.name)
                .join(', ')}
              {chapter.people.length > 8 ? ` and ${chapter.people.length - 8} more` : ''}
            </span>
          </div>
        )}
      </div>
      <div className="sb-drift">
        {hero && <Drifting photo={hero} onPick={onPick} bare />}
        {chapter.notes}
        {rest.map((photo) => (
          <Drifting key={photo.id} photo={photo} onPick={onPick} />
        ))}
        {chapter.coda}
      </div>
    </section>
  )
}

// A photo drifting up over the chapter, with its caption and date written on the print's white edge, the way a
// family writes on the back of a photo but where it can be read. The chapter's own first photo is already behind
// everything, so only its caption drifts, written straight onto it (or nothing, when it has none; its year is the
// chapter's).
function Drifting({
  photo,
  onPick,
  bare = false,
}: {
  photo: AlbumPhoto
  onPick: (p: AlbumPhoto) => void
  bare?: boolean
}) {
  const words = bare
    ? photo.caption && <Typed text={photo.caption} className="sb-caption block text-xl leading-snug font-medium" />
    : (photo.caption || photo.takenOn) && (
        <span className="sb-print-words block text-[0.9375rem] leading-snug">
          {photo.caption && <Typed text={photo.caption} className="block" />}
          {photo.takenOn && <span className="block text-xs text-sheet-muted">{showDate(photo.takenOn)}</span>}
        </span>
      )
  if (bare && !words) return <div aria-hidden="true" />
  return (
    <button
      type="button"
      onClick={() => onPick(photo)}
      aria-label={`Pick up ${photo.caption || 'this photo'}`}
      className="block cursor-pointer text-left outline-none focus-visible:ring-[3px] focus-visible:ring-ring/60"
    >
      {bare ? (
        words
      ) : (
        <span className={cn('sb-print', words && 'sb-print-captioned')}>
          <StoryImg photo={photo} sizes="(min-width: 44rem) 34rem, 78vw" />
          {words}
        </span>
      )}
    </button>
  )
}

// Words that Play types out as it reaches them, to draw the eye; anyone reading by hand sees them whole.
export function Typed({ text, className }: { text: string; className?: string }) {
  return (
    <span className={className}>
      <span className="sr-only">{text}</span>
      <span aria-hidden="true" data-type={text}>
        {text}
      </span>
    </span>
  )
}

// The end of the story: every photo in it woven together behind the story's name and the keeper's words.
function Tapestry({
  photos,
  eyebrow,
  title,
  byline,
  words,
}: {
  photos: AlbumPhoto[]
  eyebrow?: string
  title: string
  byline: string
  words?: ReactNode
}) {
  const n = photos.length
  const cols = n <= 4 ? 2 : n <= 12 ? 3 : n <= 30 ? 4 : 5
  // Enough tiles to cover a tall phone (taller still under the keeper's words), the photos repeating in turn when
  // there are fewer.
  const rows = Math.ceil(cols * 2.2) + 1 + (words ? cols + 2 : 0)
  const tiles = Array.from({ length: Math.min(TAPESTRY_MAX, Math.max(n, cols * rows)) }, (_, i) => photos[i % n]!)
  return (
    <section className="sb-tapestry" data-stop="end" aria-label={`${title}, every photo`}>
      <div className="sb-weave" style={{ '--cols': cols } as React.CSSProperties} aria-hidden="true">
        {tiles.map((photo, i) => (
          <StoryImg key={i} photo={photo} sizes={`${Math.ceil(100 / cols)}vw`} alt="" />
        ))}
      </div>
      <div className="sb-shade" />
      <div className="sb-tapestry-text flex flex-col items-center gap-2 text-center">
        {eyebrow && <p className="eyebrow text-viewer-muted">{eyebrow}</p>}
        <p className="text-display text-balance">{title}</p>
        <p className="text-sm text-viewer-muted">{byline}</p>
        {words}
      </div>
    </section>
  )
}
const TAPESTRY_MAX = 60

// The keeper's words about the album, typed out when Play reaches the end.
function Words({ text, by }: { text: string; by?: string }) {
  return (
    <div className="mt-6 flex max-w-xl flex-col gap-3">
      <Typed text={text} className="block text-lg leading-snug font-medium text-pretty sm:text-xl" />
      {by && <p className="text-sm text-viewer-muted">{by}, who keeps this album</p>}
    </div>
  )
}
